import { useCancelWorkflow } from "@src/api/ai/execute-workflows/cancel-workflow";
import { useDispatch } from "@src/store";
import { handleAiChatAction } from "@src/store/slices/aiChat";

interface ChatWorkflowWithStop {
  stop: () => void;
}

/**
 * Bundles the three concerns of stopping a streaming chat:
 *   1. Tell the backend to abort (via /cancel)
 *   2. Abort the FE fetch so the SSE reader exits
 *   3. Settle redux state to `waiting_for_input` so the spinner clears
 *      without showing an error banner
 */
export const useStopChat = (
  chatWorkflow: ChatWorkflowWithStop,
  sessionId: string | undefined
) => {
  const dispatch = useDispatch();
  const cancelWorkflow = useCancelWorkflow();

  return (source?: string, sessionIdOverride?: string) => {
    const targetSessionId = sessionIdOverride ?? sessionId;
    if (targetSessionId) {
      void cancelWorkflow
        .mutate(targetSessionId, source)
        .catch((error: unknown) => {
          // Local abort and Redux recovery already happened. Report only the
          // backend cancellation failure so it cannot become unhandled.
          // eslint-disable-next-line no-console
          console.error("[useStopChat] backend cancel failed", error);
        });
    }
    chatWorkflow.stop();
    // Stop = abandon current turn: reset to free-form text even if the
    // workflow was mid-stream
    dispatch(
      handleAiChatAction({
        type: "setNextAction",
        nextAction: {
          type: "user_input",
          variablePath: "userInput",
          prompt: "",
          userInput: { type: "text" },
        },
      })
    );
  };
};
